"use client";
import { Button } from "@/app/components/ui/button";
import { cn, formatCurrency } from "@/lib/utils";
import { Flex } from "@radix-ui/themes";
import { getNthNextBid } from "@/lib/bidding/util";
import { AuctionType, BiddingSchedule, BiddingScheduleRange } from "@prisma/client";
import MaxBidDisabledTooltip from "./MaxBidDisabledTooltip";

interface OtherBidsProps {
  hasBids: boolean;
  currentBid: number;
  biddingSchedule: BiddingSchedule & {
    BiddingScheduleRange: BiddingScheduleRange[];
  };
  disabled: boolean;
  setIsDisabled: (val: boolean) => void;
  expired: boolean;
  isCurrentUserWinner: boolean;
  onClick: (amount: number) => void;
  auctionType: AuctionType;
  floorBidder?: boolean;
}

const N_INCREMENTED_BIDS_FLOOR_BIDDER = 4;
const N_INCREMENTED_BIDS = 3;
const buttonClass = "min-w-24 text-[11pt] h-10 mb-1 flex-1";

const OtherBids = ({
  hasBids,
  currentBid,
  biddingSchedule,
  disabled,
  setIsDisabled,
  expired,
  isCurrentUserWinner,
  onClick: bid,
  auctionType,
  floorBidder = false
}: OtherBidsProps) => {
  const handleBid = (amount: number) => {
    if (expired) {
      alert("This lot has closed!");
      return;
    }
    setIsDisabled(true);
    bid(amount);
  }

  if (auctionType === AuctionType.HYBRID) {
    if (floorBidder) {
      return (
        <Flex className="w-full" justify="center" gap="3">
          {Array.from(Array(N_INCREMENTED_BIDS_FLOOR_BIDDER).keys()).map((key) => {
            if (!currentBid) return null;
            const quickBidAmount = getNthNextBid(currentBid, biddingSchedule, hasBids ? key + 1 : key);

            return (
              <div key={key} className="flex-1">
                <QuickPlaceBidButton
                  quickBidAmount={quickBidAmount}
                  onClick={() => handleBid(quickBidAmount)}
                  disabled={disabled || expired || isCurrentUserWinner}
                  compact={true}
                />
              </div>
            );
          })}
        </Flex>
      );
    }

    const quickBidAmount = getNthNextBid(currentBid, biddingSchedule, hasBids ? 1 : 0);
    return <QuickPlaceBidButton
      quickBidAmount={quickBidAmount}
      onClick={() => handleBid(quickBidAmount)}
      disabled={disabled || expired || isCurrentUserWinner}
    />
  }

  return (
    <Flex className="w-full" justify="center" gap="3">
      {Array.from(Array(N_INCREMENTED_BIDS).keys()).map((key) => {
        if (!currentBid) return null;
        const quickBidAmount = getNthNextBid(currentBid, biddingSchedule, hasBids ? key + 1 : key);

        return (
          <Button
            key={key}
            className={buttonClass}
            variant="outline"
            onClick={() => handleBid(quickBidAmount)}
            disabled={disabled || expired}
          >
            {formatCurrency(quickBidAmount)}
          </Button>
        );
      })}
    </Flex>
  );
};

const QuickPlaceBidButton = ({ quickBidAmount, onClick, disabled, compact = false }: { quickBidAmount: number, onClick: () => void, disabled: boolean, compact?: boolean }) => {
  return (
    <Button
      className={cn("bg-slate-900 text-white w-full h-10")}
      onClick={onClick}
      disabled={disabled}
    >
      {compact ? formatCurrency(quickBidAmount) : `BID ${formatCurrency(quickBidAmount)}`}
    </Button>
  )
}

export default OtherBids;
